/**
 * src/api/feedbackApi.ts — client for the Feedback Resource.
 *
 * POST  /Feedback writes via the custom Resource which trusts the session
 * for user identity — client doesn't send customer_id / email / role.
 * GET   /Feedback returns the admin-only { data, total } envelope.
 * PATCH /Feedback/<uuid> updates triage fields (admin-only). When status
 * transitions to 'shipped' with a change_id set, the server side-effects
 * a Notification row for the original author.
 */

import { getCollection, patch, post, type Collection } from './harperClient';

export type FeedbackStatus = 'new' | 'triaged' | 'planned' | 'shipped' | 'wontfix' | 'duplicate';
export type FeedbackCategory = 'bug' | 'ux' | 'feature' | 'praise' | 'other';

export interface FeedbackRecord {
  uuid: string;
  customer_id: string | null;
  customer_email: string | null;
  role: string | null;
  route: string;
  text_body: string;
  ws_log: string;        // JSON-stringified WsLogEntry[]
  user_agent: string;
  created_at: string;
  // Triage fields — null for un-triaged rows.
  status: FeedbackStatus | null;
  category: FeedbackCategory | null;
  admin_notes: string | null;
  change_id: string | null;
  triaged_at: string | null;
  // Reply thread — JSON-stringified FeedbackReply[] (admin↔customer). Absent
  // on rows that have never been replied to.
  replies?: string | null;
  last_reply_at?: string | null;
}

export interface FeedbackReply {
  from: 'admin' | 'customer';
  actor?: string;
  body: string;
  at: string;
}

export interface CreateFeedbackPayload {
  route: string;
  text_body: string;
  ws_log: string;
  user_agent: string;
}

export interface FeedbackPatch {
  status?: FeedbackStatus | null;
  category?: FeedbackCategory | null;
  admin_notes?: string | null;
  change_id?: string | null;
}

export async function createFeedback(payload: CreateFeedbackPayload): Promise<string> {
  const res = await post<{ uuid: string }>('/Feedback', payload);
  return res.uuid;
}

export async function listFeedback(): Promise<FeedbackRecord[]> {
  const res: Collection<FeedbackRecord> = await getCollection<FeedbackRecord>('/Feedback');
  const rows = res.data ?? [];
  return rows.slice().sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''));
}

export async function updateFeedback(uuid: string, body: FeedbackPatch): Promise<FeedbackRecord> {
  return patch<FeedbackRecord>(`/Feedback/${uuid}`, body);
}

/**
 * Admin: reply to a feedback submission. Appends to the thread server-side and
 * emails the customer from the support address. `emailed` is false (with
 * `emailError`) when the send failed but the reply was still recorded.
 */
export async function replyToFeedback(
  feedbackId: string,
  body: string,
): Promise<{ success: boolean; emailed: boolean; emailError: string | null; replies: FeedbackReply[] }> {
  return post('/ReplyToFeedback', { feedbackId, body });
}
